/**
 * FragmentMode Context
 *
 * Provides a global toggle for "Fragment Discovery Mode" similar to TMF7 Yii.
 * When enabled, shows visual markers on all FragmentGate components for easy
 * configuration and discovery.
 *
 * @since 2026-01-09
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// ============================================================
// Types
// ============================================================

interface FragmentModeContextValue {
  /**
   * Whether fragment discovery mode is enabled.
   * When true, shows visual markers on all FragmentGate components.
   */
  isFragmentModeEnabled: boolean;

  /**
   * Toggle fragment discovery mode on/off.
   */
  toggleFragmentMode: () => void;

  /**
   * Enable fragment discovery mode.
   */
  enableFragmentMode: () => void;

  /**
   * Disable fragment discovery mode.
   */
  disableFragmentMode: () => void;

  /**
   * Open the fragment editor modal for a specific fragment code.
   */
  openFragmentEditor: (fragmentCode: string) => void;

  /**
   * Currently selected fragment code for editing.
   */
  selectedFragmentCode: string | null;

  /**
   * Close the fragment editor modal.
   */
  closeFragmentEditor: () => void;
}

// ============================================================
// Context
// ============================================================

const FragmentModeContext = createContext<FragmentModeContextValue | null>(null);

const STORAGE_KEY = 'tmf_fragment_mode_enabled';

// ============================================================
// Provider
// ============================================================

interface FragmentModeProviderProps {
  children: ReactNode;
}

export function FragmentModeProvider({ children }: FragmentModeProviderProps) {
  // Load initial state from localStorage
  const [isFragmentModeEnabled, setIsFragmentModeEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const [selectedFragmentCode, setSelectedFragmentCode] = useState<string | null>(null);

  // Persist state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, isFragmentModeEnabled.toString());
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to save fragment mode state:', error);
    }
  }, [isFragmentModeEnabled]);

  const toggleFragmentMode = () => {
    setIsFragmentModeEnabled((prev) => !prev);
  };

  const enableFragmentMode = () => {
    setIsFragmentModeEnabled(true);
  };

  const disableFragmentMode = () => {
    setIsFragmentModeEnabled(false);
  };

  const openFragmentEditor = (fragmentCode: string) => {
    setSelectedFragmentCode(fragmentCode);
  };

  const closeFragmentEditor = () => {
    setSelectedFragmentCode(null);
  };

  const value: FragmentModeContextValue = {
    isFragmentModeEnabled,
    toggleFragmentMode,
    enableFragmentMode,
    disableFragmentMode,
    openFragmentEditor,
    selectedFragmentCode,
    closeFragmentEditor,
  };

  return (
    <FragmentModeContext.Provider value={value}>
      {children}
    </FragmentModeContext.Provider>
  );
}

// ============================================================
// Hook
// ============================================================

/**
 * Hook to access fragment mode state and controls.
 *
 * @example
 * ```tsx
 * const { isFragmentModeEnabled, toggleFragmentMode } = useFragmentMode();
 *
 * return (
 *   <button onClick={toggleFragmentMode}>
 *     Fragments: {isFragmentModeEnabled ? 'ON' : 'OFF'}
 *   </button>
 * );
 * ```
 */
export function useFragmentMode(): FragmentModeContextValue {
  const context = useContext(FragmentModeContext);
  if (!context) {
    throw new Error('useFragmentMode must be used within a FragmentModeProvider');
  }
  return context;
}
